/**
 * LIQUID-GLASS-01 (Seth 2026-09-29: "modern liquid glass look… interactive
 * touch-tilt effect on the glass surface"). A pointer-driven 3D tilt plus a
 * moving specular sheen for the m-card glass: the card leans toward your
 * thumb and a soft light follows it, like tipping a pane of glass under a
 * lamp. Implementation notes:
 *  - Writes CSS variables straight onto the element (no React re-renders,
 *    60fps on an iPhone; the CSS lives in index.css under `.m-tilt`).
 *  - Springs back on release (transition handled in CSS).
 *  - Fully disabled under Reduce Motion, and harmless on mouse hover.
 * Usage:
 *   const tilt = useTouchTilt();
 *   <div ref={tilt.ref} {...tilt.handlers} className="m-card m-tilt">…</div>
 */
import { useMemo, useRef } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

const reduceMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function useTouchTilt(maxDeg = 5) {
  const ref = useRef<HTMLElement | null>(null);
  return useMemo(() => {
    const set = (rx: number, ry: number, gx: number, gy: number, on: boolean) => {
      const el = ref.current;
      if (!el) return;
      el.style.setProperty("--tilt-x", `${rx.toFixed(2)}deg`);
      el.style.setProperty("--tilt-y", `${ry.toFixed(2)}deg`);
      el.style.setProperty("--sheen-x", `${gx.toFixed(1)}%`);
      el.style.setProperty("--sheen-y", `${gy.toFixed(1)}%`);
      el.style.setProperty("--sheen-o", on ? "1" : "0");
    };
    const move = (e: ReactPointerEvent) => {
      if (reduceMotion()) return;
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / Math.max(1, r.width); // 0..1 across
      const py = (e.clientY - r.top) / Math.max(1, r.height); // 0..1 down
      const ry = (px - 0.5) * 2 * maxDeg; // lean left/right
      const rx = (0.5 - py) * 2 * maxDeg; // lean toward/away
      set(rx, ry, px * 100, py * 100, true);
    };
    const reset = () => set(0, 0, 50, 0, false);
    return {
      ref: (el: HTMLElement | null) => {
        ref.current = el;
      },
      handlers: {
        onPointerDown: move,
        onPointerMove: move,
        onPointerUp: reset,
        onPointerLeave: reset,
        onPointerCancel: reset,
      },
    };
  }, [maxDeg]);
}
